import { useEffect, useState } from 'react';
import { InfoIcon, TableList, Typography } from 'seagull';
import { ErrorResponse, ServerError } from "../../../infra/ErrorResponse";
import AccountingCustomerDropdown from "../AccountingCustomerDropdown/AccountingCustomerDropdown";
import { OptionType } from "../AccountingCustomerDropdown/AccountingCustomerDropdown.utils";
import { FailureReasons } from "./MapContactsModal.utils";
import { selectors } from "../../../store/state-selectors";
import { useStore, useStoreActions } from "../../../store/store-context";
import { getAccountingDetails } from "../accounting.utils";
import { MapStatusView } from "../MapStatusView";
import styles from "./MapContactsRow.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function MapContactsRow(props) {
  const {
    anchorContact,
    accountingCustomers,
    integratedAccountingCustomer,
    changeContactLink,
    createNewCustomer,
    testid,
    onInputFocus,
    onInputBlur,
    accounting
  } = props;
  const [mapStatus, setMapStatus] = useState(getContactMapStatus(anchorContact, accounting));
  const [failureReason, setFailureReason] = useState(null);
  const vendorId = useStore(selectors.user.businessId);
  const userId = useStore(selectors.user.id);
  const {
    logBI
  } = useStoreActions();
  const {
    testIdPrefix,
    shortName,
    fullName,
    biCodes
  } = getAccountingDetails(accounting);
  useEffect(() => {
    setMapStatus(getContactMapStatus(anchorContact, accounting));
  }, [anchorContact, accounting]);
  const changeMap = async accountingCustomerId => {
    setMapStatus('mapping');
    setFailureReason(null);
    try {
      await changeContactLink(anchorContact, accountingCustomerId);
    } catch (err) {
      setMapStatus('failedToMap');
      const isDuplicateError = new ErrorResponse(err).status === 409;
      setFailureReason(isDuplicateError ? FailureReasons.DuplicateMapping : FailureReasons.General);
    }
  };
  const createNew = async () => {
    setFailureReason(null);
    setMapStatus('mapping');
    try {
      await createNewCustomer(anchorContact);
    } catch (e) {
      setMapStatus('failedToMap');
      const isDuplicateError = new ErrorResponse(e).hasServerError(ServerError.DuplicateNameExists);
      setFailureReason(isDuplicateError ? FailureReasons.DuplicateNew : FailureReasons.General);
    }
  };
  const onAccountingCustomerChange = async option => {
    if (!option) {
      logBI(biCodes.unlinkContact, {
        vendorId,
        userId
      });
      changeMap();
      return;
    }
    switch (option.type) {
      case OptionType.Existing:
        {
          logBI(biCodes.linkContact, {
            vendorId,
            userId
          });
          changeMap(option.customer.id());
          break;
        }
      case OptionType.CreateNew:
        {
          logBI(biCodes.pushNewContact, {
            vendorId,
            userId
          });
          createNew();
          break;
        }
    }
  };
  return /*#__PURE__*/_jsx(TableList.Row, {
    "data-testid": testid,
    items: [{
      value: /*#__PURE__*/_jsxs("div", {
        className: styles.anchorContact,
        "data-testid": "anchor-contact",
        children: [/*#__PURE__*/_jsx(Typography, {
          ellipsis: true,
          children: "".concat(anchorContact.companyName())
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsxs("div", {
            className: styles.anchorContactSecondLine,
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "paragraph2",
              ellipsis: true,
              children: "".concat(anchorContact.firstName(), " ").concat(anchorContact.lastName())
            }), anchorContact.email() ? /*#__PURE__*/_jsx(Typography, {
              ellipsis: true,
              variant: "paragraph2",
              children: "".concat(anchorContact.email())
            }) : null]
          })
        })]
      })
    }, {
      value: /*#__PURE__*/_jsx("div", {
        "data-testid": "".concat(testIdPrefix, "-contact"),
        className: styles.autocompleteWrapper,
        children: /*#__PURE__*/_jsx(AccountingCustomerDropdown, {
          accounting: accounting,
          accountingCustomers: accountingCustomers,
          value: integratedAccountingCustomer ? {
            type: OptionType.Existing,
            customer: integratedAccountingCustomer
          } : null,
          onChange: onAccountingCustomerChange,
          onInputFocus: onInputFocus,
          onInputBlur: onInputBlur
        })
      })
    }, {
      value: /*#__PURE__*/_jsxs("div", {
        className: styles.status,
        children: [/*#__PURE__*/_jsx(MapStatusView, {
          mapStatus: mapStatus
        }), failureReason === FailureReasons.DuplicateMapping && /*#__PURE__*/_jsx(InfoIcon, {
          "data-testid": "duplicate-contact-map-info",
          info: /*#__PURE__*/_jsxs(_Fragment, {
            children: ["This ", shortName, " customer is already synced", /*#__PURE__*/_jsx("br", {}), "with another Anchor contact"]
          })
        }), failureReason === FailureReasons.DuplicateNew && /*#__PURE__*/_jsx(InfoIcon, {
          "data-testid": "duplicate-contact-new-info",
          info: /*#__PURE__*/_jsxs(_Fragment, {
            children: ["Could not create a new client in ", fullName, ". A client with these details already exists"]
          })
        })]
      })
    }]
  });
}
export const getContactMapStatus = (contact, accounting) => contact.integratedId({
  accounting
}) ? 'mapped' : 'notMapped';